import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCombobox, useMultipleSelection } from 'downshift';
import { ToggleLayer, ToggleLayerOptions } from 'react-laag';
import { TextInputProps } from './TextInput';
import { CommonInputProps } from './CommonInputProps';
import {
  ArrayIfMultiElseNullable,
  intl,
  intlFmt,
  StringRecordSubset
} from '../../lib';
import {
  ComboboxDropdownMenu,
  ComboboxDropdownMenuProps,
  ComboboxInputElement,
  DEFAULT_COMBOBOX_LAYER_PLACEMENT
} from './internal';
import { ClassNameProp } from '../common-types';

/**
 * Props for a {@link Select} component.
 *
 * The `SafeItem` gymnastics ensure that the given `itemValueProp` and `itemLabelProp` are string properties of `Item`.
 * Please do not override the SafeItem type parameter.
 *
 * The `Variant` gymnastics are necessary to distinguish between single and multi select safely, in order to correctly type value and onChange with array types or nullable types, respectively.
 *
 * @author agd
 * @author Bennett Piater
 */
export interface SelectProps<
  Item,
  Variant extends 'single' | 'multi',
  SafeItem = StringRecordSubset<Item>
> extends Pick<
      CommonInputProps<ArrayIfMultiElseNullable<Variant, Item>>,
      'label' | 'error' | 'onBlur' | 'required' | 'disabled'
    >,
    Pick<TextInputProps, 'placeholder'>,
    Partial<Pick<ComboboxDropdownMenuProps<SafeItem>, 'renderItem'>>,
    ClassNameProp {
  /**
   * The variant of select that should be used.
   */
  variant: Variant;
  /**
   * The selectable items.
   */
  items: SafeItem[];
  /**
   * The property of `Item` to use to distinguish items.
   */
  itemValueProp: keyof SafeItem;
  /**
   * The property of `Item` to use as human-readable label.
   */
  itemLabelProp: keyof SafeItem;
  /**
   * @see {@link CommonInputProps.value}
   */
  value: ArrayIfMultiElseNullable<Variant, SafeItem>;
  /**
   * Callback handler for when the selectedItems changes.
   * @param selectedItems the selected items
   */
  onChange: (selectedItems: ArrayIfMultiElseNullable<Variant, Item>) => void;
  /**
   * Optional CSS classes to add to the downshift menu.
   */
  menuClassName?: string;
  /**
   * On true, onBlur will be triggered when the value changes. Only for "single" variant.
   *
   * One of the use-cases is when the form is validated with onBlur:
   *    since the common behavior of combobox is to call only onChange with item select or deselect,
   *    the error in combobox does not disappear or appear right away when the value is invalid or valid respectively.
   *    This property solves the issue.
   */
  triggerBlurOnChange?: boolean;
  /**
   * Options to configure the ToggleLayer component
   */
  menuOptions?: Partial<Omit<ToggleLayerOptions, 'ResizeObserver'>>;

  /**
   * Max number of shown selected items. When a user selects more, additionally to selected items, there will be shown
   * just a number indicating how many more items are selected.
   */
  tagLimit?: number;
}

/**
 * A combobox select component.
 * Depending on the props this can be either a single or a multi select.
 *
 * `Variant` is an internal type param needed to relate the types of the variant, value and onChange props.
 *
 * TODO: allow deselecting if multi == false
 * TODO: disabled items?, fixed items?
 *
 * @typeParam T the type of the selectable items.
 * @param properties a {@link SelectProps} object
 * @return the created {@link JSX.Element}.
 * @author agd
 * @author Bennett Piater
 * @constructor
 */
// overload for single-select (value type)
// eslint-disable-next-line @typescript-eslint/ban-types
export function Select<T extends object>(
  properties: SelectProps<T, 'single'> & {
    variant: 'single';
    value: StringRecordSubset<T> | null;
    onChange: (selectedItems: T | null) => void;
  }
): JSX.Element;

// overload for multi-select (array type)
// eslint-disable-next-line @typescript-eslint/ban-types
export function Select<T extends object>(
  properties: SelectProps<T, 'multi'> & {
    variant: 'multi';
    value: StringRecordSubset<T>[];
    onChange: (selectedItems: T[]) => void;
  }
): JSX.Element;

// eslint-disable-next-line @typescript-eslint/ban-types
export function Select<T extends object, Variant extends 'single' | 'multi'>({
  itemLabelProp,
  itemValueProp,
  className = '',
  menuClassName = '',
  triggerBlurOnChange = false,
  menuOptions = {},
  renderItem = (item: StringRecordSubset<T>) => item[itemLabelProp],
  ...props
}: SelectProps<T, Variant>): JSX.Element {
  // type of individual items
  type ItemType = StringRecordSubset<T>;
  // local state of the text entered into the input field for filtering
  const [inputValue, setInputValue] = useState('');
  const itemToString = useCallback(item => item?.[itemLabelProp] ?? '', [
    itemLabelProp
  ]);

  // setup Downshift
  const {
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems,
    setSelectedItems
  } = useMultipleSelection<ItemType>({
    getA11yRemovalMessage({ removedSelectedItem }) {
      return intlFmt('common.item.removed', itemToString(removedSelectedItem));
    },
    // this is necessary so that removeSelectedItem knows how to announce the change to the screen reader
    itemToString,
    selectedItems:
      props.variant === 'single' && props.value !== null
        ? ([props.value] as ItemType[])
        : props.variant === 'single'
        ? ([] as ItemType[])
        : (props.value as ItemType[]),
    onSelectedItemsChange: async ({ selectedItems }) => {
      // This cast is necessary because we are essentially downcasting.
      if (props.variant === 'single') {
        if (triggerBlurOnChange) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          //  if the form validates values with onBlur event,
          //  we need to ensure that it has correct values at the moment of validation.
          //  calling only onChange will not set the field to touch and
          //  as a result will not trigger form validation.
          await props.onChange((selectedItems?.[0] as T) ?? null);
          props.onBlur?.();
        } else {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          props.onChange((selectedItems?.[0] as T) ?? null);
        }
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        props.onChange((selectedItems ?? []) as T[]);
      }
    }
  });

  // update the inputValue state
  useEffect(() => {
    if (props.variant === 'single') {
      setInputValue(
        props.value ? (props.value as ItemType)[itemLabelProp] : ''
      );
    } else {
      setInputValue('');
    }
  }, [itemLabelProp, props.value, props.variant]);

  // get the filtered items depending on the state of the inputValue
  const filteredItems = useMemo(() => {
    if (props.variant === 'multi') {
      return props.items.filter(
        item =>
          !inputValue ||
          item[itemLabelProp].toLowerCase().includes(inputValue.toLowerCase())
      );
    } else {
      return props.items.filter(
        item =>
          !inputValue ||
          selectedItems.find(item => item[itemLabelProp] === inputValue) ||
          item[itemLabelProp].toLowerCase().includes(inputValue.toLowerCase())
      );
    }
  }, [itemLabelProp, props.items, selectedItems, inputValue, props.variant]);

  // handler for when the input value changes
  // this is a workaround to get around a Downshift bug (https://github.com/downshift-js/downshift/issues/1108)
  // (instead of using useCombobox.stateChangeTypes.InputChange in the switch further down)
  // TODO: remove the workaround once Downshift fixed the issue on their side
  const onInputChange = useCallback(value => setInputValue(value), []);

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    selectItem,
    toggleMenu,
    openMenu,
    getToggleButtonProps
  } = useCombobox({
    inputValue,
    items: filteredItems,
    itemToString,
    getA11ySelectionMessage({ selectedItem }) {
      if (selectedItem) {
        return intlFmt('common.item.selected', itemToString(selectedItem));
      }
      return '';
    },
    getA11yStatusMessage({ isOpen, resultCount, previousResultCount }) {
      if (isOpen) {
        if (!resultCount) {
          return intl('common.search.noentry');
        }
        if (resultCount !== previousResultCount) {
          return intlFmt('common.item.navigate', resultCount);
        }
      }
      return '';
    },
    scrollIntoView(item) {
      item?.scrollIntoView({ block: 'nearest' });
    },
    stateReducer: (_state, { changes, type }) => {
      if (props.variant === 'single') {
        return changes;
      }

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            // keep the menu open after selection
            isOpen: true,
            // with the previously selected option highlighted
            highlightedIndex: Math.max(
              0,
              props.items.indexOf(changes.selectedItem)
            )
          };
        default:
          return changes;
      }
    },

    // "type" is missing in the types of onStateChange which is why this ignore is needed
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    onStateChange: ({ type, selectedItem }) => {
      // function to handle what should happen when the element loses focus
      const handleBlur = () => {
        if (props.variant === 'multi') {
          selectItem((null as unknown) as ItemType);
          setInputValue('');
        } else {
          setInputValue(selectedItems?.[0]?.[itemLabelProp] ?? '');
        }
      };

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem) {
            const multi = props.variant === 'multi';
            setInputValue(multi ? '' : selectedItem?.[itemLabelProp] ?? '');
            selectItem((null as unknown) as ItemType);
            if (selectedItems.includes(selectedItem)) {
              removeSelectedItem(selectedItem);
              break;
            }
            if (multi) {
              addSelectedItem(selectedItem);
            } else {
              setSelectedItems([selectedItem]);
            }
            // WCAG aria-live special selection announcement for downshift library
            setTimeout(() => {
              selectItem(selectedItem);
              setTimeout(() => selectItem((null as unknown) as ItemType), 201);
            }, 1);
          }
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
        case useCombobox.stateChangeTypes.InputBlur:
          props.onBlur?.();
          handleBlur();
          break;
        case useCombobox.stateChangeTypes.FunctionToggleMenu:
          if (isOpen) {
            handleBlur();
          }
          break;
        default:
          break;
      }
    }
  });

  return (
    <ToggleLayer
      isOpen={isOpen && !props.disabled}
      closeOnOutsideClick={true}
      placement={DEFAULT_COMBOBOX_LAYER_PLACEMENT}
      renderLayer={({ isOpen, layerProps, triggerRect }): JSX.Element => (
        <ComboboxDropdownMenu<ItemType>
          loading={false} // we don't have a loading state
          selectableItems={filteredItems}
          selectedItems={selectedItems}
          highlightedIndex={highlightedIndex}
          itemToKey={(item: ItemType) => item[itemValueProp]}
          renderItem={renderItem}
          layerProps={layerProps}
          triggerRect={triggerRect}
          isOpen={isOpen}
          getMenuProps={getMenuProps}
          getItemProps={getItemProps}
          wasLoaded={true}
          className={menuClassName}
        />
      )}
      {...menuOptions}
    >
      {({ triggerRef, isOpen }): JSX.Element => (
        <ComboboxInputElement<ItemType>
          triggerRef={triggerRef}
          variant={props.variant}
          selectedItems={selectedItems}
          isOpen={isOpen}
          getDropdownProps={getDropdownProps}
          removeSelectedItem={removeSelectedItem}
          placeholder={props.placeholder}
          getInputProps={getInputProps}
          toggleMenu={toggleMenu}
          openMenu={openMenu}
          getComboboxProps={getComboboxProps}
          disabled={props.disabled}
          getLabelProps={getLabelProps}
          tagLimit={props.tagLimit}
          error={props.error}
          label={props.label}
          itemToString={itemToString}
          required={props.required}
          showSearchIcon={false}
          showExpandTool={true}
          elementClickBehavior="toggleMenu"
          getToggleButtonProps={getToggleButtonProps}
          className={className}
          onInputChange={onInputChange}
        />
      )}
    </ToggleLayer>
  );
}
